import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "@/auth/SessionProvider";
import { useApiClient } from "@/hooks/useApi";
import { EmptyState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { isApiError, type ApiClient } from "@/lib/api";
import { AudienceActions } from "./AudienceActions";

interface Preview { poolMembers: number; sourceCount: number }
const clientIds = new WeakMap<ApiClient, number>();
let nextClientId = 0;
function clientId(client: ApiClient): number {
  const known = clientIds.get(client);
  if (known !== undefined) return known;
  const id = ++nextClientId;
  clientIds.set(client, id);
  return id;
}
function parsePreview(value: unknown, campaignKey: string): Preview {
  if (typeof value !== "object" || value === null || !("audience" in value)) throw new Error("Audience preview cannot be verified.");
  const audience = value.audience;
  if (typeof audience !== "object" || audience === null || !("campaign_key" in audience) ||
      typeof audience.campaign_key !== "string" || audience.campaign_key.trim().length === 0 || audience.campaign_key.length > 200 || audience.campaign_key !== campaignKey ||
      !("pool_members" in audience) || !("source_count" in audience) ||
      !Number.isSafeInteger(audience.pool_members) || !Number.isSafeInteger(audience.source_count) ||
      (audience.pool_members as number) < 0 || (audience.source_count as number) < 0) throw new Error("Audience preview cannot be verified.");
  return { poolMembers: audience.pool_members as number, sourceCount: audience.source_count as number };
}

export function AudienceTab({ campaignId, campaignKey }: { campaignId: string; campaignKey: string }) {
  const api = useApiClient();
  const { session, orgId, subscribeScopeInvalidation } = useSession();
  const actor = session?.user.id ?? null;
  const owner = useMemo(() => clientId(api), [api]);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    const unsubscribe = subscribeScopeInvalidation(() => { if (active) setRevision((current) => current + 1); });
    return () => { active = false; unsubscribe(); };
  }, [subscribeScopeInvalidation]);
  const preview = useQuery({
    queryKey: ["audience-preview", actor, orgId, campaignId, campaignKey, owner, revision],
    enabled: Boolean(actor && orgId && campaignId && campaignKey),
    queryFn: async ({ signal }) => parsePreview(await api.get<unknown>("/radar/audience/preview", { query: { campaign_id: campaignId }, signal }), campaignKey),
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  if (!actor || !orgId) return <EmptyState title="Choose an organisation" body="Select your organisation to view its assigned campaign audience." />;
  const denied = preview.isError && isApiError(preview.error) && [401, 403].includes(preview.error.httpStatus);
  return (
    <section aria-labelledby="audience-title" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 id="audience-title" className="text-xl font-semibold text-ink">Audience preview</h2><p className="mt-1 text-sm text-ink-2">The shared audience pool for this assigned campaign.</p></div>
        <Button type="button" variant="secondary" className="min-h-[44px] min-w-[44px]" disabled={preview.isFetching} onClick={() => void preview.refetch()}>Refresh preview</Button>
      </div>
      <div aria-live="polite" aria-busy={preview.isFetching}>
        {preview.isFetching || preview.isPending ? <LoadingState label="Checking audience preview…" /> : preview.isError ? (
          <EmptyState tone="error" title={denied ? "Audience access unavailable" : "Audience preview unavailable"} body={denied ? "Your current organisation or campaign access could not be verified. Check your assignment before trying again." : "The preview could not be verified. Refresh to check again; no audience action was requested."} />
        ) : preview.data ? (
          <div className="rounded-xl border border-line p-5">
            <dl className="grid gap-5 sm:grid-cols-2"><div><dt className="text-sm text-ink-2">Unique pool members</dt><dd className="mt-1 text-2xl font-semibold tabular-nums text-ink">{preview.data.poolMembers.toLocaleString("en-GB")}</dd></div><div><dt className="text-sm text-ink-2">Source groups</dt><dd className="mt-1 text-2xl font-semibold tabular-nums text-ink">{preview.data.sourceCount.toLocaleString("en-GB")}</dd></div></dl>
            {preview.data.poolMembers === 0 ? <p className="mt-4 text-sm text-ink-2">No audience members are available in this preview.</p> : null}
            <p className="mt-4 text-sm text-ink-2">Source groups can overlap. The pool total is not a per-platform export count.</p>
          </div>
        ) : null}
      </div>
      {/* AU09-UI: the accepted placeholder card is replaced by the wired AU05-AU08
        action/state surface (the read-only preview above is untouched). */}
      <AudienceActions campaignId={campaignId} campaignKey={campaignKey} />
    </section>
  );
}
